/**
 * @fileoverview Input/output schemas for the chapter-extractor-chunked-v2 agent.
 *
 * This agent is designed for processing large documents in chunks. It takes:
 * - A chunk of text to analyze
 * - Previously extracted chapters (partialChapters)
 * - Chunk metadata for context
 *
 * It outputs a merged list of chapters, combining new finds with existing ones.
 * Only fully-contained chapters are extracted; partial chapters at chunk
 * boundaries are ignored (they'll be picked up in the next chunk's overlap).
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { ChapterImportSchema } from '@/domains/chapter-workspace/schemas';
import { ChunkInfoSchema } from '@/lib/content-chunking';

// =============================================================================
// PREVIOUSLY EXTRACTED CHAPTER SUMMARY (lightweight, for dedup context)
// =============================================================================

/**
 * A lightweight summary of a previously extracted chapter.
 * Passed to the agent so it can skip chapters it has already processed.
 */
export const PreviousChapterSummarySchema = z.object({
  number: z.number().describe('The chapter number.'),
  title: z.string().describe('The chapter title.'),
  chapterHeading: z.string().describe('The exact chapter heading as it appears in the text.'),
  firstLine: z.string().describe('The first line of the chapter content.'),
});

export type PreviousChapterSummary = z.infer<typeof PreviousChapterSummarySchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  /**
   * The current chunk of text to analyze.
   * May include overlap from adjacent chunks for context continuity.
   */
  textChunk: z.string().describe('The current chunk of text to analyze for chapters.'),

  /**
   * Lightweight summaries of chapters already extracted from previous chunks.
   * Empty array for the first chunk.
   * The agent uses this to avoid re-extracting chapters that span into the overlap region.
   */
  previousChapters: z.array(PreviousChapterSummarySchema).describe(
    'Summaries of chapters already extracted from previous chunks. Empty for first chunk. Agent should skip these.'
  ),

  /**
   * Metadata about this chunk's position in the document.
   * Helps the agent understand context (first chunk, middle, last).
   */
  chunkInfo: ChunkInfoSchema.describe('Metadata about this chunk position in the full document.'),
}).merge(AGENT_SCHEMA_OBJECTS.storyContext.schema);

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Summary of what changed in this chunk (useful for debugging and UI feedback).
 */
export const ChunkProcessingSummarySchema = z.object({
  /** Number of new chapters discovered in this chunk */
  newCount: z.number().min(0).describe('Number of new chapters discovered in this chunk (integer >= 0).'),

  /** Number of chapters that were skipped because they were already extracted */
  skippedCount: z.number().min(0).describe('Number of chapters skipped (already extracted in previous chunks, integer >= 0).'),
}).describe('Summary of chapter extraction results for this chunk.');

export type ChunkProcessingSummary = z.infer<typeof ChunkProcessingSummarySchema>;

export const OutputSchema = z.object({
  /**
   * New chapters extracted from this chunk only.
   * Does NOT include previously extracted chapters — only newly discovered ones.
   * Partial chapters (those that are cut off at chunk boundaries) must be excluded.
   */
  chapters: z.array(ChapterImportSchema).describe(
    'Newly extracted chapters from this chunk. Excludes previously extracted chapters and partial chapters cut off at chunk boundaries.'
  ),

  /**
   * Optional summary of what changed in this chunk.
   * Useful for progress tracking and debugging.
   */
  summary: ChunkProcessingSummarySchema.optional().describe(
    'Optional summary of extraction results for this chunk.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
